import { z } from 'zod'
import type { DraftDefinitie, DraftRowField, DraftSection, DraftField } from './draft.js'
import { Definitie } from './definitie.js'
import type { TextDraft } from './text.js'

/**
 * De enige brug van de conceptboom naar de strikte `Definitie`.
 *
 * `verhard` doet twee dingen in één keer: hij meldt elk gat dat een concept nog heeft,
 * en als er geen gaten zijn levert hij de definitie op die kan publiceren. Dat is
 * precies de vorm die de publicatiepoort nodig heeft (D-27, D-46) en die het
 * eigenschappenpaneel nodig heeft om te tonen wat er nog ontbreekt (D-13).
 *
 * **Er is geen tweede uitgang.** Een concept wordt nooit ergens anders tot definitie
 * gemaakt, want dan zou er een tweede plek zijn waar staat wat compleet betekent.
 *
 * **Wat "zichtbaar" in R-145 betekent.** R-145 blokkeert publiceren zolang een
 * vertaling van een zichtbaar veld ontbreekt. Dat onderscheidt een label dat de
 * invuller leest van een interne aanduiding, en niet een veld dat nu wordt getoond van
 * een veld dat een zichtbaarheidsregel nu verbergt: die regel hangt van antwoorden af
 * en die zijn er bij het publiceren nog niet. Elk tekstelement in de definitie telt
 * dus mee, ook dat van een veld dat pas bij een bepaalde keuze verschijnt.
 *
 * **Een optionele tekst die er staat moet af zijn.** Een lege hulptekst is geen
 * hulptekst; de configurator laat de sleutel dan weg in plaats van twee lege talen te
 * sturen. Staat hij er half in, met alleen Nederlands, dan is dat een gat en geen
 * afwezigheid: iemand is eraan begonnen. `verhard` verandert de inhoud niet, hij
 * beoordeelt hem alleen.
 */

/**
 * De zes soorten bevinding waarop publiceren kan blokkeren.
 *
 * Drie ervan noemt D-27 met zoveel woorden: een ontbrekende vertaling (R-145), een
 * gebroken verwijzing (D-40, R-60) en een ongeldige regel (D-21). Daar komen `toets`
 * bij uit D-62 en `compartiment` uit D-69, die beide naar D-27 verwijzen zonder diens tekst te
 * wijzigen.
 *
 * **`vorm` is de zesde en staat in geen eis.** Een formulier kan onaf zijn op een
 * manier die D-27 niet benoemt: een keuzeveld zonder opties, een sectie zonder velden,
 * een criterium zonder schaal. Dat blokkeert publiceren net zo goed, want er is dan
 * geen `Definitie` om te bevriezen. Het staat hier als eigen soort in plaats van
 * weggemoffeld bij een van de vijf, want de inrichter moet kunnen zien dat zijn
 * formulier nog niet af is en niet dat er iets ongeldig is.
 */
export const GATE_CHECKS = ['vorm', 'vertaling', 'regel', 'verwijzing', 'toets', 'compartiment'] as const
export type GateCheck = (typeof GATE_CHECKS)[number]

/** Eén ontbrekend of half ingevuld ding, met het pad waar het staat. */
export const Gap = z.strictObject({
  /** Onder welke controle van de publicatiepoort dit valt. */
  controle: z.enum(GATE_CHECKS),
  /** Het pad in de conceptstand, zoals Zod het ook opschrijft: `['secties', 0, 'titel', 'en']`. */
  pad: z.array(z.union([z.string(), z.number().int()])),
  /** Wat er mist, in de taal van de inrichter. */
  wat: z.string().min(1),
})
export type Gap = z.infer<typeof Gap>

export type Hardening = { ok: true; definitie: Definitie } | { ok: false; gaten: Gap[] }

const LANGUAGES = [
  { sleutel: 'nl' as const, naam: 'de Nederlandse' },
  { sleutel: 'en' as const, naam: 'de Engelse' },
]

function empty(value: string): boolean {
  return !/\S/.test(value)
}

/** Een tekst die er hoort te staan: elke lege taal is een gat. */
function required(text: TextDraft, path: (string | number)[], what: string): Gap[] {
  return LANGUAGES.filter(({ sleutel: key }) => empty(text[key])).map(({ sleutel: key, naam: name }) => ({
    controle: 'vertaling' as const,
    pad: [...path, key],
    wat: `${name} ${what} ontbreekt`,
  }))
}

/** Een gat in de vorm: het formulier is nog niet af. */
function shape(path: (string | number)[], what: string): Gap {
  return { controle: 'vorm', pad: path, wat: what }
}

/**
 * Een tekst die weggelaten mag worden. Helemaal leeg is een gat, want dan had de
 * sleutel er niet moeten staan; half leeg is een gat omdat iemand eraan begonnen is.
 */
function optional(
  text: TextDraft | undefined,
  path: (string | number)[],
  what: string,
): Gap[] {
  if (text === undefined) return []
  if (empty(text.nl) && empty(text.en)) {
    return [{ controle: 'vertaling', pad: path, wat: `${what} is leeg; laat hem weg of vul hem in` }]
  }
  return required(text, path, what)
}

/** De opties van een keuzeveld: minstens één, elk met een waarde en een label, geen dubbele waarden. */
function optionGaps(
  options: readonly { waarde: string; label: TextDraft }[] | undefined,
  path: (string | number)[],
  requiredList: boolean,
): Gap[] {
  if (options === undefined) return []
  const gaps: Gap[] = []
  if (requiredList && options.length === 0) {
    gaps.push(shape(path, 'dit keuzeveld heeft nog geen opties'))
  }
  const seen = new Map<string, number>()
  options.forEach((option, i) => {
    if (empty(option.waarde)) {
      gaps.push(shape([...path, i, 'waarde'], 'deze optie heeft nog geen waarde'))
    } else {
      const earlier = seen.get(option.waarde)
      if (earlier !== undefined) {
        gaps.push(
          shape([...path, i, 'waarde'], `de waarde ${option.waarde} staat ook op optie ${earlier + 1}`),
        )
      } else {
        seen.set(option.waarde, i)
      }
    }
    gaps.push(...required(option.label, [...path, i, 'label'], 'optielabel'))
  })
  return gaps
}

function sourceGaps(
  source: { contract: string; pad: string } | undefined,
  path: (string | number)[],
  requiredSource: boolean,
): Gap[] {
  if (source === undefined) {
    return requiredSource ? [shape(path, 'er is nog geen dossierveld gekozen')] : []
  }
  const gaps: Gap[] = []
  if (empty(source.contract)) {
    gaps.push(shape([...path, 'contract'], 'er is nog geen bouwblok gekozen'))
  }
  if (empty(source.pad)) {
    gaps.push(shape([...path, 'pad'], 'er is nog geen pad in het contract gekozen'))
  }
  return gaps
}

function fieldGaps(field: DraftField | DraftRowField, path: (string | number)[]): Gap[] {
  const gaps: Gap[] = []

  if (field.control === 'component') {
    gaps.push(...optional(field.label, [...path, 'label'], 'kop'))
    return gaps
  }

  gaps.push(...required(field.label, [...path, 'label'], 'label'))
  gaps.push(...optional(field.hulptekst, [...path, 'hulptekst'], 'hulptekst'))

  switch (field.control) {
    case 'dropdown':
    case 'multiselect':
    case 'radio':
      gaps.push(...optionGaps(field.opties, [...path, 'opties'], true))
      break
    case 'checkbox':
      // Een checkbox zonder opties is één losse instemming en dus geen gat (`veld.ts`).
      gaps.push(...optionGaps(field.opties, [...path, 'opties'], false))
      break
    case 'subgrid':
      gaps.push(...sourceGaps(field.bron, [...path, 'bron'], false))
      break
    case 'verwijzing':
      gaps.push(...sourceGaps(field.bron, [...path, 'bron'], true))
      break
    case 'herhaalgroep':
      if (field.rijVelden.length === 0) {
        gaps.push(shape([...path, 'rijVelden'], 'deze herhaalgroep heeft nog geen velden'))
      }
      field.rijVelden.forEach((rowField, r) => {
        gaps.push(...fieldGaps(rowField, [...path, 'rijVelden', r]))
      })
      break
    case 'criterium':
      if (field.criteriumId === undefined) {
        // De aanduiding is buiten het formulier bruikbaar (R-150) en wordt door een
        // mens gekozen, dus er valt er geen te genereren.
        gaps.push(shape([...path, 'criteriumId'], 'dit criterium heeft nog geen aanduiding'))
      }
      if (field.schaal === undefined) {
        gaps.push(shape([...path, 'schaal'], 'dit criterium heeft nog geen schaal'))
      } else if (field.schaal.soort === 'tweekeuze') {
        if (field.schaal.opties !== undefined) {
          gaps.push(...optionGaps(field.schaal.opties, [...path, 'schaal', 'opties'], true))
        }
      } else {
        const range: (string | number)[] = [...path, 'schaal']
        if (field.schaal.minimum === undefined) {
          gaps.push(shape([...range, 'minimum'], 'de ondergrens van de schaal ontbreekt'))
        }
        if (field.schaal.maximum === undefined) {
          gaps.push(shape([...range, 'maximum'], 'de bovengrens van de schaal ontbreekt'))
        }
        field.schaal.waarden?.forEach((value, w) => {
          gaps.push(...required(value.label, [...range, 'waarden', w, 'label'], 'schaallabel'))
        })
      }
      if (field.onderbouwing !== undefined) {
        gaps.push(
          ...required(
            field.onderbouwing.label,
            [...path, 'onderbouwing', 'label'],
            'label van de onderbouwing',
          ),
        )
      }
      break
    default:
      break
  }

  return gaps
}

function sectionGaps(section: DraftSection, path: (string | number)[]): Gap[] {
  const gaps: Gap[] = [
    ...required(section.titel, [...path, 'titel'], 'sectietitel'),
    ...optional(section.uitleg, [...path, 'uitleg'], 'uitleg'),
  ]
  if (section.velden.length === 0) {
    gaps.push(shape([...path, 'velden'], 'deze sectie heeft nog geen velden'))
  }
  section.velden.forEach((field, v) => {
    gaps.push(...fieldGaps(field, [...path, 'velden', v]))
  })
  return gaps
}

export function harden(draft: DraftDefinitie): Hardening {
  const gaps: Gap[] = [...required(draft.titel, ['titel'], 'formuliertitel')]

  if (draft.secties.length === 0) {
    gaps.push(shape(['secties'], 'dit formulier heeft nog geen secties'))
  }
  draft.secties.forEach((section, s) => {
    gaps.push(...sectionGaps(section, ['secties', s]))
  })

  if (gaps.length > 0) return { ok: false, gaten: gaps }

  // Alle gaten dicht, dus nu de samenhang: unieke id's, een regeldoel dat bestaat,
  // een bronsectie met alleen verwijzingen, `gaatTerug` alleen in een toetssectie.
  // Die controles staan in `Definitie` en worden hier niet herhaald.
  const outcome = Definitie.safeParse(draft)
  if (outcome.success) return { ok: true, definitie: outcome.data }
  return { ok: false, gaten: outcome.error.issues.map(toGap) }
}

/**
 * Een Zod-bevinding uit `Definitie` als gat, met zijn controle afgeleid uit het pad.
 *
 * De `superRefine` van `Definitie` weet zelf niet onder welke controle van de poort zijn
 * bevindingen vallen; dat zou een tweede plek zijn waar de indeling staat. Het pad zegt
 * het wel: een bevinding op een regel is een regelgat (D-21), een op `compartiment` een
 * compartimentgat (D-69), en de rest is vorm. Elke tak heeft zijn eigen test.
 */
function toGap(problem: z.core.$ZodIssue): Gap {
  const path = [...problem.path] as (string | number)[]
  const check: GateCheck = path.includes('regels')
    ? 'regel'
    : path.includes('compartiment')
      ? 'compartiment'
      : 'vorm'
  return { controle: check, pad: path, wat: problem.message }
}
